import { Helmet } from "react-helmet-async";
import { COMPANY } from "@/config/company";

interface ArticleMetaProps {
  title: string;
  description: string;
  author: string;
  publishedAt: string;
  imageUrl?: string;
  url: string;
  keywords?: string[];
  canonicalUrl?: string;
}

export function ArticleMeta({
  title,
  description,
  author,
  publishedAt,
  imageUrl,
  url,
  keywords = [],
  canonicalUrl,
}: ArticleMetaProps) {
  // Enforce title ≤ 60 chars. Append suffix only if it fits.
  const SUFFIX = ' | TrustedHeal';
  const rawTitle = title.trim();
  let fullTitle: string;
  if ((rawTitle + SUFFIX).length <= 60) {
    fullTitle = rawTitle + SUFFIX;
  } else if (rawTitle.length <= 60) {
    fullTitle = rawTitle; // drop suffix, title still fits on its own
  } else {
    fullTitle = rawTitle.substring(0, 57) + '...';
  }

  // Enforce description ≤ 155 chars
  const safeDescription = description && description.length > 155
    ? description.substring(0, 155)
    : description;

  const ogImage = imageUrl || `${COMPANY.website}/og-default.jpg`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={safeDescription} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      <meta name="author" content={author || "TrustedHeal"} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl || url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="article" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="article:published_time" content={publishedAt} />
      <meta property="article:author" content={author || "TrustedHeal"} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={safeDescription} />
      <meta property="twitter:image" content={ogImage} />
    </Helmet>
  );
}
